import BenchlingConfig from 'BenchlingConfig';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

// Define UI order for different chat types.
// GraphQL enums don't preserve order, so we define it explicitly here.
export const ORDERED_CHAT_TYPES = [ChatType.DEEP_RESEARCH, ChatType.DATA_CAPTURE];

export function isAISidepanelEnabled(): boolean {
  return getChatTypes().length > 0;
}

export function getChatTypes(): Array<ChatType> {
  return ORDERED_CHAT_TYPES.filter((chatType) => isChatTypeEnabled(chatType));
}

export function getDefaultChatType(): ChatType {
  return getChatTypes()[0] ?? ChatType.DEEP_RESEARCH;
}

export function isChatTypeEnabled(chatType: ChatType): boolean {
  switch (chatType) {
    case ChatType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
    case ChatType.DATA_CAPTURE:
      return BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT;
    default:
      return false;
  }
}

/**
 * Parse the value out of a URL like/chats/new?agent=<agentModeQSValue> (see: AIRoutes.ts).
 *
 * NOTE: These URLs may be put in marketing emails, so we should avoid changing the string values
 * when possible, i.e. maintain backwards compatibility. It is acceptable to have multiple querystring
 * values map to the same AgentMode.
 */
export function getAgentModeFromQueryStringValue(agentModeQSValue: string | null): AgentMode | null {
  switch (agentModeQSValue) {
    case 'ask':
      return AgentMode.ASK;
    case 'deep-research':
      return AgentMode.DEEP_RESEARCH;
    case 'compose':
      return AgentMode.DATA_CAPTURE;
    default:
      return null;
  }
}

export function getChatTypeText(chatType: ChatType): string {
  switch (chatType) {
    case ChatType.DEEP_RESEARCH:
      return 'deep research';
    case ChatType.DATA_CAPTURE:
      return 'Compose';
    default:
      throw new Error(`Unknown chat type: ${assertNever(chatType)}`);
  }
}

export function doesChatTypePreferExpandedSidepanel(chatType: ChatType): boolean {
  switch (chatType) {
    case ChatType.DATA_CAPTURE:
      return true;
    case ChatType.DEEP_RESEARCH:
      return false;
    default:
      assertNever(chatType);
      return false;
  }
}

/** @deprecated Use {@link AgentType} when possible */
export enum AgentMode {
  ASK = 'ASK',
  DEEP_RESEARCH = 'DEEP_RESEARCH',
  DATA_CAPTURE = 'DATA_CAPTURE',
}

export const ChatTypeByAgentMode: Record<AgentMode, ChatType> = {
  [AgentMode.DEEP_RESEARCH]: ChatType.DEEP_RESEARCH,
  [AgentMode.ASK]: ChatType.DEEP_RESEARCH,
  [AgentMode.DATA_CAPTURE]: ChatType.DATA_CAPTURE,
};

export const ChatTypeByAgentType: Record<AgentType, ChatType> = {
  [AgentType.DEEP_RESEARCH]: ChatType.DEEP_RESEARCH,
  [AgentType.ASK]: ChatType.DEEP_RESEARCH,
  [AgentType.DATA_CAPTURE]: ChatType.DATA_CAPTURE,
};

// Temporary helper function to map AgentMode to AgentType
// Should be removed once we have a proper mapping in the backend
export function mapAgentModeToAgentType(agentMode: AgentMode): AgentType {
  switch (agentMode) {
    case AgentMode.ASK:
      return AgentType.ASK;
    case AgentMode.DEEP_RESEARCH:
      return AgentType.DEEP_RESEARCH;
    case AgentMode.DATA_CAPTURE:
      return AgentType.DATA_CAPTURE;
    default:
      assertNever(agentMode);
      throw new Error(`Unexpected AgentMode ${assertNever(agentMode)}`);
  }
}

export function getAgentModeIcon(agentMode: AgentMode): BenchlingIconName | null {
  switch (agentMode) {
    case AgentMode.DEEP_RESEARCH:
      return 'file-magnifying-glass';
    case AgentMode.DATA_CAPTURE:
      return 'pen';
    case AgentMode.ASK:
      return 'comments';
    default:
      assertNever(agentMode);
      return null;
  }
}

export function getAgentModeText(agentMode: AgentMode): string {
  switch (agentMode) {
    case AgentMode.DEEP_RESEARCH:
      return 'Deep Research';
    case AgentMode.ASK:
      return 'Ask';
    case AgentMode.DATA_CAPTURE:
      return 'Compose';
    default:
      assertNever(agentMode);
      throw new Error(`Unknown agent mode: ${assertNever(agentMode)}`);
  }
}

export function isAgentModeEnabled(agentMode: AgentMode): boolean {
  switch (agentMode) {
    case AgentMode.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
    case AgentMode.DATA_CAPTURE:
      return BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT;
    case AgentMode.ASK:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
    default:
      assertNever(agentMode);
      return false;
  }
}

export function getAvailableAgents(chatTypes: Array<ChatType>): Array<AgentMode> {
  return Object.values(AgentMode).filter(
    (agentMode) => isAgentModeEnabled(agentMode) && chatTypes.includes(ChatTypeByAgentMode[agentMode])
  );
}

export function getAgentModePlaceholderText(agentMode: AgentMode): string {
  switch (agentMode) {
    case AgentMode.DEEP_RESEARCH:
      return 'Write detailed reports based on your data';
    case AgentMode.ASK:
      return 'Ask questions about your data';
    case AgentMode.DATA_CAPTURE:
      return 'Draft an entry or template';
    default:
      assertNever(agentMode);
      return '';
  }
}

/**
 * doesAgentTypeSupportSources must also return true to allow public data to be used.
 */
export function doesAgentTypeSupportPublicData(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH_PUBLIC_DATA;
    case AgentType.ASK:
      return false;
    case AgentType.DATA_CAPTURE:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Indicate if the agent backend supports setting CreateDisplayMessageChatEvent['sources'].
 */
export function doesAgentTypeSupportSources(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return true;
    case AgentType.DATA_CAPTURE:
      return false;
    case AgentType.ASK:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Indicate if the chat type should show progress UI (tool calls, activity tracking).
 *
 * TODO: This should be unconditionally true.
 */
export function doesChatTypeShowProgress(chatType: ChatType): boolean {
  switch (chatType) {
    case ChatType.DEEP_RESEARCH:
      return true;
    case ChatType.DATA_CAPTURE:
      return true;
    default:
      assertNever(chatType);
      return false;
  }
}
